import React from 'react';
import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  IconButton
} from '@chakra-ui/react';
import { Handle, Position } from 'reactflow';
import {
  FiMousePointer,
  FiEdit,
  FiCopy,
  FiTrash2,
  FiClock,
} from 'react-icons/fi';
import { LuMousePointerClick } from 'react-icons/lu';

const ButtonClickedNode = ({ data }) => {
  const buttons = data.buttons || [{ name: 'Button 1', id: 'button_1' }];
  const activeButtons = buttons.filter(
    (btn) => btn.name && btn.name.trim() !== ''
  );

  const getButtonColor = (index) => {
    const colors = [
      {
        bg: 'var(--brand-primary)',
        text: 'var(--brand-primary)',
        lightBg: 'var(--brand-primary)33',
      },
      {
        bg: '#8B5CF6',
        text: '#8B5CF6',
        lightBg: '#ad46ff33',
      },
      {
        bg: '#10B981',
        text: '#10B981',
        lightBg: '#00c95133',
      },
    ];
    return colors[index] || colors[0];
  };

  return (
    <Box
      bg="white"
      borderWidth="2px"
      borderColor="#7c86ff"
      borderRadius="xl"
      boxShadow={data.selected ? 'xl' : 'lg'}
      minW="240px"
      position="relative"
    >
      {/* TARGET HANDLE - Single input */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          width: '12px',
          height: '12px',
          background: 'var(--brand-primary)',
          border: '2px solid white',
        }}
      />

      {/* HEADER */}
      <Flex
        bgGradient="linear(to-r, #4f39f6, #615fff)"
        color="white"
        px={4}
        py={2.5}
        borderTopRadius="xl"
        align="center"
        gap={2}
      >
        <FiMousePointer size={16} />
        <Text noOfLines={1} flex={1} fontSize="sm" fontWeight="medium">
          Button Clicked
        </Text>

        <HStack spacing={1}>
          <IconButton
            size="xs"
            variant="ghost"
            color="white"
            icon={<FiEdit size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              data.onEdit?.(data.id);
            }}
          />
          <IconButton
            size="xs"
            variant="ghost"
            color="white"
            icon={<FiCopy size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              data.onDuplicate?.(data.id);
            }}
          />
          <IconButton
            size="xs"
            variant="ghost"
            color="white"
            icon={<FiTrash2 size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              data.onDelete?.(data.id);
            }}
          />
        </HStack>
      </Flex>

      {/* BODY */}
      <Box p={3}>
        <Text fontSize="sm" fontWeight="medium" mb={3}>
          {data.label || 'Button Clicked'}
        </Text>

        <Text fontSize="xs" color="gray.500" mb={3}>
          {activeButtons.length} button
          {activeButtons.length !== 1 ? 's' : ''} configured
        </Text>

        <VStack spacing={3} align="stretch">
          {activeButtons.map((button, index) => {
            const color = getButtonColor(index);
            const hasCondition = button.conditionEnabled;

            return (
              <Box
                key={button.id}
                borderWidth="1px"
                borderRadius="lg"
                p={2}
                bg={hasCondition ? color.lightBg : 'gray.50'}
                position="relative"
              >
                {/* BUTTON HEADER */}
                <HStack spacing={2} mb={2}>
                  <Box
                    w="10px"
                    h="10px"
                    borderRadius="full"
                    bg={color.bg}
                    border="2px solid white"
                  />
                  <Text
                    fontSize="xs"
                    fontWeight="medium"
                    flex={1}
                    noOfLines={1}
                  >
                    {button.name}
                  </Text>
                  <LuMousePointerClick size={14} color={color.text} />
                </HStack>

                {/* CONDITION INFO */}
                {hasCondition && (
                  <HStack
                    spacing={1}
                    fontSize="10px"
                    color="#bb4d00"
                    bg="#fffbeb"
                    px={1.5}
                    py={1}
                    borderRadius="md"
                    mb={2}
                  >
                    <FiClock size={10} />
                    <Text>
                      {button.conditionTime} {button.conditionUnit} check
                    </Text>
                  </HStack>
                )}

                {/* IF BUTTON HAS TIME CONDITION - Show True/False handles */}
                {hasCondition ? (
                  <>
                    {/* TRUE HANDLE - Button clicked in time */}
                    <HStack spacing={2} mb={2} position="relative" pr={8}>
                      <Box
                        w="6px"
                        h="6px"
                        borderRadius="full"
                        bg="green.500"
                      />
                      <Text fontSize="10px" fontWeight="medium" color="green.700">
                        Clicked In Time
                      </Text>
                      <Handle
                        type="source"
                        position={Position.Right}
                        id={`${button.id}_true`}
                        className="branch-handle"
                        style={{
                          width: '10px',
                          height: '10px',
                          background: '#22c55e',
                          border: '2px solid white',
                          top: '0%',
                          transform: 'none',
                          // See flowHandles.css .branch-handle. This dot sits 3 levels
                          // deep (row HStack -> button card border+p={2}=9 -> body
                          // p={3}=12 -> node border=2) plus half the rendered 10px dot
                          // (5): -(9+12+2+5) = -28px reaches the node's real edge.
                          '--flow-branch-offset': '-28px',
                        }}
                        title={`${button.name} - Clicked in time`}
                      />
                    </HStack>

                    {/* FALSE HANDLE - Button NOT clicked in time */}
                    <HStack spacing={2} position="relative" pr={8}>
                      <Box
                        w="6px"
                        h="6px"
                        borderRadius="full"
                        bg="red.500"
                      />
                      <Text fontSize="10px" fontWeight="medium" color="red.700">
                        Not Clicked In Time
                      </Text>
                      <Handle
                        type="source"
                        position={Position.Right}
                        id={`${button.id}_false`}
                        className="branch-handle"
                        style={{
                          width: '10px',
                          height: '10px',
                          background: '#ef4444',
                          border: '2px solid white',
                          top: '0%',
                          transform: 'none',
                          '--flow-branch-offset': '-28px',
                        }}
                        title={`${button.name} - Not clicked in time`}
                      />
                    </HStack>
                  </>
                ) : (
                  // SIMPLE BUTTON CLICK - Show only clicked handle
                  <HStack spacing={2} position="relative" pr={8}>
                    <Box
                      w="6px"
                      h="6px"
                      borderRadius="full"
                      bg={color.bg}
                    />
                    <Text fontSize="10px" fontWeight="medium" color="gray.700">
                      Clicked
                    </Text>
                    <Handle
                      type="source"
                      position={Position.Right}
                      id={`${button.id}_clicked`}
                      className="branch-handle"
                      style={{
                        width: '10px',
                        height: '10px',
                        background: color.bg,
                        border: '2px solid white',
                        top: '0%',
                        transform: 'none',
                        '--flow-branch-offset': '-28px',
                      }}
                      title={`${button.name} - Clicked`}
                    />
                  </HStack>
                )}
              </Box>
            );
          })}

          {/* NO CLICK FALLBACK SECTION */}
          {data.noClickWaitEnabled && (
            <Box borderTopWidth="1px" pt={3}>
              <Box borderWidth="1px" borderRadius="lg" p={2} bg="gray.50" position="relative">
                <HStack spacing={2} mb={2}>
                  <FiClock size={14} color="#6b7280" />
                  <Text fontSize="xs" fontWeight="medium" color="gray.700" flex={1}>
                    No Button Clicked
                  </Text>
                </HStack>

                <HStack
                  spacing={1}
                  fontSize="10px"
                  color="amber.700"
                  bg="amber.50"
                  px={1.5}
                  py={1}
                  borderRadius="md"
                  mb={2}
                >
                  <FiClock size={10} />
                  <Text>
                    {data.noClickWaitTime} {data.noClickWaitUnit}
                  </Text>
                </HStack>

                <HStack spacing={2} position="relative" pr={8}>
                  <Box w="6px" h="6px" borderRadius="full" bg="gray.400" />
                  <Text fontSize="10px" fontWeight="medium" color="gray.600">
                    No Click Path
                  </Text>

                  <Handle
                    type="source"
                    position={Position.Right}
                    id="no_click"
                    className="branch-handle"
                    style={{
                      width: '10px',
                      height: '10px',
                      background: '#9ca3af',
                      border: '2px solid white',
                      top: '0%',
                      transform: 'none',
                      '--flow-branch-offset': '-28px',
                    }}
                    title="No Click Fallback"
                  />
                </HStack>
              </Box>
            </Box>
          )}
        </VStack>
      </Box>
    </Box>
  );
};

export default ButtonClickedNode;